import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { CTASection } from '@/components/CTASection';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { SkeletonList } from '@/components/SkeletonList';
import { getGearLibraryBySlug } from '@/lib/api';
import { GEAR_LIBRARY_DETAIL_PAGE } from '@/constants/testIds';
import { keyedList } from '@/lib/keys';
import NotFound from '@/pages/NotFound';

const TYPE_LABEL = { camera: 'Camera Body', lens: 'Lens', lighting: 'Lighting & Grip', article: 'Technique' };

export default function GearLibraryDetail() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setEntry(null);
    setNotFound(false);
    getGearLibraryBySlug(slug)
      .then(setEntry)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound />;

  const structuredData = entry
    ? {
        '@context': 'https://schema.org',
        '@type': entry.type === 'article' ? 'Article' : 'Product',
        name: entry.brand ? `${entry.brand} ${entry.title}` : entry.title,
        description: entry.summary,
        brand: entry.brand || undefined,
      }
    : undefined;

  return (
    <div data-testid={GEAR_LIBRARY_DETAIL_PAGE.root}>
      {entry && <SEO title={`${entry.brand ? entry.brand + ' ' : ''}${entry.title}`} description={entry.summary} structuredData={structuredData} />}
      {entry ? (
        <PageHero
          kicker={TYPE_LABEL[entry.type] || 'Gear Guide'}
          title={(entry.brand ? `${entry.brand} ${entry.title}` : entry.title).toUpperCase()}
          subtitle={entry.summary}
          height="min-h-[40vh] lg:min-h-[46vh]"
        />
      ) : (
        <div className="min-h-[40vh] lg:min-h-[46vh] bg-[#0F0F10]" />
      )}

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
          <Link
            to="/gear"
            data-testid={GEAR_LIBRARY_DETAIL_PAGE.backLink}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Gear Guide
          </Link>

          {!entry && <SkeletonList count={5} className="h-4 w-full rounded bg-[#141416]" />}

          {entry && (
            <Reveal>
              {entry.good_for?.length > 0 && (
                <div data-testid={GEAR_LIBRARY_DETAIL_PAGE.goodForList} className="flex flex-wrap gap-1.5 mb-8">
                  {entry.good_for.map((tag) => (
                    <Badge key={tag} className="bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)] font-normal">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              <article className="prose prose-invert max-w-none">
                {keyedList(entry.content.split('\n\n')).map(({ key, item: paragraph }) => (
                  <p key={key} className="text-[hsl(var(--lxra-fg))]/90 leading-relaxed mb-5 whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </article>

              {Object.keys(entry.specs || {}).length > 0 && (
                <div className="mt-10">
                  <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--lxra-gold))] mb-4">Specifications</p>
                  <div data-testid={GEAR_LIBRARY_DETAIL_PAGE.specsTable} className="rounded-xl border border-[hsl(var(--lxra-border))] overflow-hidden">
                    {Object.entries(entry.specs).map(([k, v], i) => (
                      <div
                        key={k}
                        className={`flex items-start justify-between gap-4 px-4 py-3 text-sm ${i % 2 === 0 ? 'bg-[#0F0F10]' : 'bg-[#141416]'}`}
                      >
                        <span className="text-[hsl(var(--lxra-muted))]">{k}</span>
                        <span className="text-[hsl(var(--lxra-fg))] text-right">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>
          )}
        </div>
      </section>

      <CTASection testId="gear-library-detail-cta-banner" buttonTestId="gear-library-detail-cta-book-now-button" />
    </div>
  );
}
